import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {JsonSchema} from '../schema-form.types';

/**
 * x-widget:formula → free-text math expression plus insertable Var/Operator chips.
 * The number of Var chips comes from `x-varCount` (default 2); the widget stays
 * generic (no module-specific logic).
 */
@Component({
  selector: 'sf-formula',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-2 w-full">
      <input
        type="text"
        [value]="value ?? ''"
        [placeholder]="placeholder"
        (input)="valueChange.emit($any($event.target).value)"
        class="w-full px-3 py-2 text-sm font-mono text-gray-900 bg-gray-50 border border-gray-300 rounded-lg
               focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-colors"/>
      <div class="flex flex-wrap gap-2">
        @for (v of vars; track v) {
          <button type="button" (click)="insert(v)"
                  class="px-2.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-300 transition-colors">
            {{ v }}
          </button>
        }
        @for (op of operators; track op) {
          <button type="button" (click)="insert(op)"
                  class="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200 transition-colors">
            {{ op }}
          </button>
        }
      </div>
    </div>
  `,
})
export class SfFormulaComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  readonly operators = ['+', '-', '*', '/', '^', '(', ')'];

  get vars(): string[] {
    const count = Number(this.schema?.['x-varCount'] ?? 2) || 0;
    return Array.from({length: count}, (_, i) => `Var${i + 1}`);
  }

  get placeholder(): string {
    return this.schema?.['x-placeholder'] ?? 'z.B. Var1 * Var2';
  }

  insert(token: string): void {
    const current = (this.value ?? '').toString();
    const next = current && !current.endsWith(' ') ? `${current} ${token}` : `${current}${token}`;
    this.valueChange.emit(next);
  }
}
